/**
 * One row of the Kehitys board.
 *
 * Kind is an icon, status is a coloured pill — two different facts get two
 * different encodings rather than two competing hues. Attachment thumbnails
 * appear in the row on purpose: a report with a screenshot is actionable
 * immediately, and that is worth knowing before opening it.
 */

import { Button } from "@/components/ui/button";
import { useAttachmentUrl } from "@/hooks/useAttachmentUrl";
import { CommentIcon, DeleteIcon } from "@/lib/icons";
import {
  FEEDBACK_KIND_META,
  FEEDBACK_STATUS_META,
  formatDuration,
} from "@/lib/feedbackMeta";
import { cn } from "@/lib/utils";
import type { FeedbackAttachment, FeedbackReport } from "@/types";

import { VoteButton } from "./VoteButton";

/** Row-level thumbnail. Its own component so the hook is not called in a loop. */
function RowThumb({ shot }: { shot: FeedbackAttachment }) {
  const { url, onError } = useAttachmentUrl(shot.thumbUrl);

  if (!url) {
    return <span className="block h-full w-full rounded border border-dashed bg-muted" />;
  }

  return (
    <img
      src={url}
      alt={shot.caption ?? ""}
      loading="lazy"
      onError={onError}
      className="h-full w-full rounded border object-cover"
    />
  );
}

interface FeedbackRowProps {
  report: FeedbackReport;
  onOpen: (id: string) => void;
  onToggleVote: (id: string) => void;
  /** Server-computed `viewerCanDelete` — the report's author, INTERNAL+ or ADMIN. */
  canDelete: boolean;
  onRequestDelete: (report: FeedbackReport, event: React.MouseEvent) => void;
}

/** "3 päivää sitten" and friends, without pulling in a date library. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "tänään";
  if (days === 1) return "eilen";
  if (days < 14) return `${days} päivää sitten`;
  const weeks = Math.floor(days / 7);
  if (weeks < 9) return `${weeks} viikkoa sitten`;
  return `${Math.floor(days / 30)} kuukautta sitten`;
}

export function FeedbackRow({
  report,
  onOpen,
  onToggleVote,
  canDelete,
  onRequestDelete,
}: FeedbackRowProps) {
  const kind = FEEDBACK_KIND_META[report.kind];
  const status = FEEDBACK_STATUS_META[report.status];
  const KindIcon = kind.icon;
  const author = report.createdBy
    ? `${report.createdBy.firstName ?? ""} ${report.createdBy.lastName ?? ""}`.trim()
    : "";
  const shots = report.attachments ?? [];

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen(report.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(report.id);
        }
      }}
      className={cn(
        "grid cursor-pointer grid-cols-[3.5rem_minmax(0,1fr)_auto] items-stretch",
        "overflow-hidden rounded-lg border bg-card shadow-sm",
        "hover:border-muted-foreground/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary",
      )}
    >
      <VoteButton
        voteCount={report.voteCount}
        hasVoted={report.viewerHasVoted}
        onToggle={() => onToggleVote(report.id)}
      />

      <div className="flex min-w-0 flex-col gap-1 px-3.5 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <KindIcon
            className={cn("h-4 w-4 shrink-0", kind.iconClass)}
            aria-label={kind.label}
          />
          <span className="truncate text-sm font-semibold">{report.title}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span className="font-mono text-[11px] opacity-70">{report.reference}</span>
          <span aria-hidden>·</span>
          <span>{author}</span>
          <span aria-hidden>·</span>
          <span>{relativeTime(report.createdAt)}</span>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1">
            <CommentIcon className="h-3.5 w-3.5" />
            {report.commentCount}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3 py-2.5 pl-1 pr-3.5">
        {shots.length > 0 && (
          <div className="hidden gap-1 sm:flex">
            {shots.slice(0, 2).map((shot) => (
              <span key={shot.id} className="relative block h-6 w-[34px]">
                <RowThumb shot={shot} />
                {shot.mediaType === "VIDEO" && (
                  // A poster frame is indistinguishable from a screenshot, so
                  // video has to be marked. The duration doubles as the marker.
                  <span className="absolute inset-0 grid place-items-center rounded bg-black/45">
                    <span className="font-mono text-[8px] font-bold leading-none text-white">
                      {formatDuration(shot.durationSeconds) || "▶"}
                    </span>
                  </span>
                )}
              </span>
            ))}
            {shots.length > 2 && (
              <span className="grid h-6 w-[34px] place-items-center rounded border border-dashed font-mono text-[10px] text-muted-foreground">
                +{shots.length - 2}
              </span>
            )}
          </div>
        )}
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
            status.pill,
          )}
        >
          {status.label}
        </span>

        {/* The row itself is a click target, so the handler stops propagation —
            otherwise deleting would also open the report. */}
        {canDelete && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => onRequestDelete(report, event)}
            title="Poista ilmoitus"
            className="h-9 px-2.5 text-muted-foreground hover:bg-rose-50/60 hover:text-rose-600"
          >
            <DeleteIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
    </article>
  );
}
